<template>
  <Transition name="simple-modal">
    <div
      v-if="isModalOpen"
      class="simple-modal-overlay"
      :class="{ 'simple-modal-overlay-centered': isFullscreenPresentation }"
      @click.self="handleBackdropClick">
      <div
        ref="sheetRef"
        class="simple-modal-container"
        :class="modalSizeClass"
        :style="sheetTransformStyle">
        <!-- Drag handle - swipe to dismiss from here -->
        <div
          v-if="canDismiss && !isLoggedOutModal && !isFullscreenPresentation"
          class="flex justify-center pt-3 pb-1 cursor-grab active:cursor-grabbing"
          @touchstart="onTouchStart"
          @touchmove="onTouchMove"
          @touchend="onTouchEnd">
          <div class="w-10 h-1 rounded-full bg-white/30" />
        </div>

        <!-- Header with close button -->
        <div
          v-if="canDismiss && !isLoggedOutModal"
          class="simple-modal-header"
          @touchstart="onTouchStart"
          @touchmove="onTouchMove"
          @touchend="onTouchEnd">
          <!-- Spacer for centering -->
          <div class="w-8" />

          <!-- Step indicator for review modals (centered) -->
          <div v-if="isReviewModal" class="simple-modal-steps">
            <template v-for="step in 2" :key="step">
              <div
                class="simple-modal-step"
                :class="[
                  reviewStep === step
                    ? 'step-active'
                    : reviewStep > step
                      ? 'step-complete'
                      : 'step-pending'
                ]">
                <fa-icon
                  v-if="reviewStep > step"
                  icon="check"
                  class="text-xs" />
                <span v-else>{{ step }}</span>
              </div>
              <div
                v-if="step < 2"
                class="simple-modal-step-line"
                :class="reviewStep > step ? 'line-complete' : 'line-pending'" />
            </template>
          </div>
          <div v-else class="flex-1" />

          <button class="simple-modal-close" @click="close">
            <fa-icon :icon="['fas', 'xmark']" />
          </button>
        </div>

        <!-- Scrollable content area -->
        <div
          ref="contentRef"
          class="simple-modal-content"
          :style="keyboardStyle()"
          @touchstart="onContentTouchStart"
          @touchmove="onContentTouchMove"
          @touchend="onContentTouchEnd">
          <component
            :is="modalComponent"
            v-bind="modalProps"
            @close="close"
            @complete="complete"
            @step-change="onStepChange" />
        </div>
      </div>
    </div>
  </Transition>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted, inject } from 'vue'
import { useKeyboardAvoidance } from '@/composables/useKeyboardAvoidance'

import ModalListDelete from '@/components/Modals/ModalListDelete'
import ModalListFollow from '@/components/Modals/ModalListFollow'
import ModalListUnfollow from '@/components/Modals/ModalListUnfollow'
import ModalListCreate from '@/components/Modals/ModalListCreate'
import ModalAppFeedback from '@/components/Modals/ModalAppFeedback'
import ModalTitleShare from '@/components/Modals/ModalTitleShare'
import ModalAddToList from '@/components/Modals/ModalAddToList'
import ModalReviewCreate from '@/components/Modals/ModalReviewCreate'
import ModalReviewFormChild from '@/components/Modals/ModalReviewFormChild'
import ModalReviewEdit from '@/components/Modals/ModalReviewEdit'
import ModalReviewImageEdit from '@/components/Modals/ModalReviewImageEdit'
import ModalReviewDelete from '@/components/Modals/ModalReviewDelete'
import ModalUserWatchProviders from '@/components/Modals/ModalUserWatchProviders'
import ModalAppWelcome from '@/components/Modals/ModalAppWelcome'
import ModalAppAuthWall from '@/components/Modals/ModalAppAuthWall'
import ModalAppOnboarding from '@/components/Modals/ModalAppOnboarding'
import ModalNote from '@/components/Modals/ModalNote'
import ModalGuestActionPrompt from '@/components/Modals/ModalGuestActionPrompt'
import ModalAccountDelete from '@/components/Modals/ModalAccountDelete'
import ModalReviewSuccess from '@/components/Modals/ModalReviewSuccess'
import ModalSendToUser from '@/components/Modals/ModalSendToUser'
import ModalMessages from '@/components/Modals/ModalMessages'
import ModalTitleRecommendations from '@/components/Modals/ModalTitleRecommendations'
import ModalClubCreate from '@/components/Modals/ModalClubCreate'
import ModalClubPostOptions from '@/components/Modals/ModalClubPostOptions'
import ModalClubPostDelete from '@/components/Modals/ModalClubPostDelete'
import ModalClubWatchlistItemOptions from '@/components/Modals/ModalClubWatchlistItemOptions'
import ModalListMembers from '@/components/Modals/ModalListMembers'
import ModalListInviteAccept from '@/components/Modals/ModalListInviteAccept'
import ModalTitleStatus from '@/components/Modals/ModalTitleStatus'
import ModalUnknown from '@/components/Modals/ModalUnknown'

const props = defineProps({
  open: { type: Boolean, default: false },
  canDismiss: { type: Boolean, default: true },
  type: String,
  modalProps: Object,
  presentation: { type: String, default: 'default' }
})

// Check if this is a fullscreen presentation (used on desktop for centered large modal)
const isFullscreenPresentation = computed(() => {
  return props.presentation === 'fullscreen'
})

// Check if this is a logged-out modal that should hide the header
const isLoggedOutModal = computed(() => {
  return ['GuestActionPrompt'].includes(props.type)
})

const emit = defineEmits(['close'])
const isModalOpen = ref(false)
const reviewStep = ref(1)

const { contentStyle: keyboardStyle } = useKeyboardAvoidance()

// Swipe-to-dismiss refs
const sheetRef = ref(null)
const contentRef = ref(null)
const touchStartY = ref(0)
const currentTranslateY = ref(0)
const isDragging = ref(false)
const isContentDragging = ref(false)

// Computed style for sheet drag transform
const sheetTransformStyle = computed(() => {
  if (currentTranslateY.value > 0) {
    return {
      transform: `translateY(${currentTranslateY.value}px)`,
      transition: isDragging.value || isContentDragging.value ? 'none' : 'transform 200ms ease-out'
    }
  }
  return {}
})

// Check if this is a review modal that needs step indicator
// Note: ReviewForm has its own styled step indicator, so we disable the container's one
const isReviewModal = computed(() => {
  return false
})

const onStepChange = (step) => {
  reviewStep.value = step
}

watch(
  () => props.open,
  (value) => {
    isModalOpen.value = value
    // Reset drag state when modal opens/closes
    if (value) {
      currentTranslateY.value = 0
      isDragging.value = false
      isContentDragging.value = false
    }
  },
  { immediate: true }
)

const close = () => {
  if (!props.canDismiss) return
  isModalOpen.value = false
  emit('close')
}

const complete = () => {
  isModalOpen.value = false
  emit('close')
}

const handleBackdropClick = () => {
  if (props.canDismiss) {
    close()
  }
}

// Swipe-to-dismiss touch handlers
const onTouchStart = (e) => {
  if (!props.canDismiss) return
  touchStartY.value = e.touches[0].clientY
  currentTranslateY.value = 0
  isDragging.value = true
}

const onTouchMove = (e) => {
  if (!isDragging.value || !props.canDismiss) return

  const deltaY = e.touches[0].clientY - touchStartY.value

  // Only allow dragging down (positive deltaY)
  if (deltaY > 0) {
    currentTranslateY.value = deltaY
    e.preventDefault()
  }
}

const onTouchEnd = () => {
  if (!isDragging.value && !isContentDragging.value) return
  isDragging.value = false
  isContentDragging.value = false

  const threshold = sheetRef.value
    ? Math.min(100, sheetRef.value.offsetHeight * 0.3)
    : 100

  if (currentTranslateY.value > threshold) {
    // Animate out and close
    currentTranslateY.value = window.innerHeight
    setTimeout(() => {
      currentTranslateY.value = 0
      close()
    }, 200)
  } else {
    // Snap back
    currentTranslateY.value = 0
  }
}

// Content area drag - only when scrolled to top
const onContentTouchStart = (e) => {
  if (!props.canDismiss) return
  const content = contentRef.value
  // Only enable drag if content is scrolled to top
  if (content && content.scrollTop <= 0) {
    touchStartY.value = e.touches[0].clientY
    currentTranslateY.value = 0
    isContentDragging.value = false
  }
}

const onContentTouchMove = (e) => {
  if (!props.canDismiss) return
  const content = contentRef.value
  if (!content) return

  const deltaY = e.touches[0].clientY - touchStartY.value

  // If scrolled to top and dragging down, enable sheet drag
  if (content.scrollTop <= 0 && deltaY > 0) {
    isContentDragging.value = true
    currentTranslateY.value = deltaY
    e.preventDefault()
  }
}

const onContentTouchEnd = () => {
  if (isContentDragging.value) {
    onTouchEnd()
  }
}

const handleKeyboard = (e) => {
  if (e.key === 'Escape' && props.canDismiss) {
    close()
  }
}

onMounted(() => {
  document.addEventListener('keyup', handleKeyboard)
})

onUnmounted(() => {
  document.removeEventListener('keyup', handleKeyboard)
})

const modalComponent = computed(() => {
  switch (props.type) {
    case 'ListDelete':
      return ModalListDelete
    case 'ListFollow':
      return ModalListFollow
    case 'ListUnfollow':
      return ModalListUnfollow
    case 'ListCreate':
      return ModalListCreate
    case 'AppFeedback':
      return ModalAppFeedback
    case 'TitleShare':
      return ModalTitleShare
    case 'TitleAddToList':
    case 'AddToList':
      return ModalAddToList
    case 'TitleStatus':
      return ModalTitleStatus
    case 'ReviewCreate':
      return ModalReviewCreate
    case 'ReviewFormChild':
      return ModalReviewFormChild
    case 'ReviewEdit':
      return ModalReviewEdit
    case 'ReviewImageEdit':
      return ModalReviewImageEdit
    case 'ReviewDelete':
      return ModalReviewDelete
    case 'UserWatchProvidersEdit':
      return ModalUserWatchProviders
    case 'AppWelcome':
      return ModalAppWelcome
    case 'AppOnboarding':
      return ModalAppOnboarding
    case 'AppAuthWall':
      return ModalAppAuthWall
    case 'Note':
      return ModalNote
    case 'GuestActionPrompt':
      return ModalGuestActionPrompt
    case 'AccountDelete':
      return ModalAccountDelete
    case 'ReviewSuccess':
      return ModalReviewSuccess
    case 'SendToUser':
      return ModalSendToUser
    case 'Messages':
      return ModalMessages
    case 'TitleRecommendations':
      return ModalTitleRecommendations
    case 'ClubCreate':
      return ModalClubCreate
    case 'ClubPostOptions':
      return ModalClubPostOptions
    case 'ClubPostDelete':
      return ModalClubPostDelete
    case 'ClubWatchlistItemOptions':
      return ModalClubWatchlistItemOptions
    case 'ListMembers':
      return ModalListMembers
    case 'ListInviteAccept':
      return ModalListInviteAccept
    default:
      return ModalUnknown
  }
})

const modalSizeClass = computed(() => {
  // For fullscreen presentation on desktop, use centered large modal
  if (isFullscreenPresentation.value) {
    return 'simple-modal-fullscreen-desktop'
  }

  switch (props.type) {
    case 'ListDelete':
    case 'ListCreate':
    case 'Note':
    case 'GuestActionPrompt':
      return 'simple-modal-med simple-modal-logged-out'
    case 'AccountDelete':
    case 'ReviewSuccess':
    case 'Messages':
    case 'TitleRecommendations':
    case 'ClubCreate':
      return 'simple-modal-med'
    case 'SendToUser':
    case 'ListMembers':
    case 'ListInviteAccept':
    case 'TitleAddToList':
    case 'AddToList':
      return 'simple-modal-tall'
    case 'ReviewDelete':
      return 'simple-modal-small'
    case 'AppOnboarding':
      return 'simple-modal-large'
    case 'AppWelcome':
    case 'AppAuthWall':
      return 'simple-modal-full'
    default:
      return 'simple-modal-default'
  }
})
</script>

<style scoped>
.simple-modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  display: flex;
  align-items: flex-end;
  justify-content: center;
  z-index: 100;
}

/* Centered overlay for fullscreen presentation on desktop */
.simple-modal-overlay-centered {
  align-items: center;
}

.simple-modal-container {
  background: rgb(17 24 39);
  border-radius: 16px 16px 0 0;
  max-height: 90vh;
  width: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow:
    0 -10px 40px -12px rgba(0, 0, 0, 0.5),
    0 0 0 1px rgba(255, 255, 255, 0.1);
  /* Animation base state */
  transform: translateY(0);
  transition: transform 250ms cubic-bezier(0.32, 0.72, 0, 1);
}

.simple-modal-default {
  max-width: 600px;
}

.simple-modal-med {
  max-width: 500px;
  max-height: 95vh;
}

.simple-modal-tall {
  max-width: 500px;
  max-height: 90vh;
}

.simple-modal-small {
  max-width: 400px;
  max-height: min(300px, 90vh);
}

.simple-modal-large {
  max-width: 90%;
  max-height: 75vh;
}

.simple-modal-full {
  max-width: 100%;
  max-height: 100%;
  width: 100%;
  height: 100%;
  border-radius: 0;
}

/* Desktop fullscreen presentation - large centered modal with rounded corners */
.simple-modal-fullscreen-desktop {
  max-width: 900px;
  width: calc(100% - 48px);
  max-height: 90vh;
  border-radius: 16px;
  margin: 24px;
}

/* Add safe area padding for all modals except full-screen ones */
.simple-modal-container:not(.simple-modal-full) {
  padding-bottom: env(safe-area-inset-bottom, 0px);
}

.simple-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 4px 16px 0;
}

.simple-modal-steps {
  display: flex;
  align-items: center;
  gap: 0;
}

.simple-modal-step {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 600;
  transition: all 200ms ease;
}

.simple-modal-step.step-active {
  background: rgb(99 102 241);
  color: white;
}

.simple-modal-step.step-complete {
  background: rgb(34 197 94);
  color: white;
}

.simple-modal-step.step-pending {
  background: rgb(55 65 81);
  color: rgb(156 163 175);
}

.simple-modal-step-line {
  width: 24px;
  height: 2px;
  transition: background 200ms ease;
}

.simple-modal-step-line.line-complete {
  background: rgb(34 197 94);
}

.simple-modal-step-line.line-pending {
  background: rgb(55 65 81);
}

.simple-modal-close {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: rgba(255, 255, 255, 0.8);
  transition:
    background 150ms ease,
    transform 150ms ease;
}

.simple-modal-close:hover {
  background: rgba(255, 255, 255, 0.2);
}

.simple-modal-close:active {
  transform: scale(0.95);
}

.simple-modal-content {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 16px 24px 24px;
  -webkit-overflow-scrolling: touch;
  display: flex;
  flex-direction: column;
}

/* Remove padding for full-screen modals like AppWelcome */
.simple-modal-full .simple-modal-content {
  padding: 0;
}

/* Remove padding for logged-out modals to allow gradient bleeding */
.simple-modal-logged-out .simple-modal-content {
  padding: 0;
}

/* Transition animations - slide up from bottom */
.simple-modal-enter-active,
.simple-modal-leave-active {
  transition: opacity 250ms ease;
}

.simple-modal-enter-from,
.simple-modal-leave-to {
  opacity: 0;
}

/* Container slide animation - uses descendant selector */
.simple-modal-enter-from .simple-modal-container,
.simple-modal-leave-to .simple-modal-container {
  transform: translateY(100%);
}
</style>
